import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../../hooks'
import {
  type EnrollmentWelcomeIraData,
  type EnrollmentWelcomeIraSubmitInput,
  type EnrollmentWelcomeIraSubmitOutput,
} from '../../../../../models'

/**
 * Get Enrollment WelcomeIra data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentWelcomeIraData>
 * @example const response = await getEnrollmentWelcomeIraData()
 */
export const getEnrollmentWelcomeIraData = async (context?: DefaultContext): Promise<EnrollmentWelcomeIraData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentWelcomeIraData',
      query: `mutation getEnrollmentWelcomeIraData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentWelcomeIraData }
  return response.submitEnrollment
}

export const useGetEnrollmentWelcomeIraData = createQueryHook<undefined, EnrollmentWelcomeIraData>(
  getEnrollmentWelcomeIraData,
)

/**
 * Submit Enrollment WelcomeIra data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentWelcomeIraSubmitOutput>
 * @example const response = await submitEnrollmentWelcomeIraData();
 */
export const submitEnrollmentWelcomeIraData = async (
  enrollmentWelcomeIraSubmitInput: EnrollmentWelcomeIraSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentWelcomeIraSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentWelcomeIraData',
      query: `mutation submitEnrollmentWelcomeIraData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentWelcomeIraSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentWelcomeIraSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentWelcomeIraData = createMutationHook<
  EnrollmentWelcomeIraSubmitInput,
  EnrollmentWelcomeIraSubmitOutput
>(submitEnrollmentWelcomeIraData)
